import { deleteCookie, getCookie, setCookie } from "@/lib/cookies";
import { getSlug } from "@/lib/utils";
import axios from "axios";
import {
  QueryKey,
  UseInfiniteQueryOptions,
  useInfiniteQuery as useTanStackInfiniteQuery,
} from "@tanstack/react-query";

declare module "axios" {
  export interface AxiosRequestConfig {
    /** Opt out of the global 403 redirect-home in `handleApiError` so a
     * permission-gated surface can render its own restricted state. The
     * error is still resolved into `{error, errorStatus}` — callers must
     * detect that shape themselves. */
    suppress403Redirect?: boolean;
  }
}

const env = import.meta.env.VITE_ENV;
export const API_URL = import.meta.env.VITE_API_URL;
export const TOKEN_KEY_NAME = env === "qa" ? "CFIQ_QA_TOKEN" : "CFIQ_TOKEN";
export const DEVICE_ID_NAME =
  env === "qa" ? "CFIQ_QA_DEVICE_ID" : "CFIQ_DEVICE_ID";
export const CF_ORGANIZATION_KEY_NAME =
  env === "qa" ? "CFIQ_QA_ORGANIZATION" : "CFIQ_ORGANIZATION";
const COMMONFORGE_ORG_HEADER = "X-FORGEIQ-COMPANY-ID";
const COMMONFORGE_SLUG_HEADER = "X-FORGEIQ-SLUG";
const IMPERSONATE_HEADER = "X-FORGEIQ-IMPERSONATE";
export const IMPERSONATE_KEY_NAME =
  env === "qa" ? "FORGEIQ_QA_IMPERSONATE" : "FORGEIQ_IMPERSONATE";
const RESPONSE_TOKEN_TIME_HEADER = "X-Seconds-Until-Expiry";
const AI_CAP_STATUS_HEADER = "X-AI-Cap-Status";
const AI_CAP_STATUS_UPDATED_HEADER = "X-AI-Cap-Status-Updated";
export const TOKEN_EXPIRY_LOCAL_STORAGE_KEY = "cf_token_expiry_time";
export const AI_CAP_STATUS_LOCAL_STORAGE_KEY = "cf_ai_cap_status";
export const WEBAPP_DOMAIN = import.meta.env.VITE_WEBAPP_DOMAIN;
export const WEBAPP_PORT = import.meta.env.VITE_WEBAPP_PORT;
export const WEBAPP_PROTOCOL = import.meta.env.VITE_WEBAPP_PROTOCOL;
export let TOP_HOST = `${WEBAPP_DOMAIN}`;
if (WEBAPP_PORT) {
  if (
    !(TOP_HOST === "qa.commonforgeiq.com" || TOP_HOST === "commonforgeiq.com")
  ) {
    TOP_HOST += `:${WEBAPP_PORT}`;
  }
}

export type Files = FileList | File[] | Readonly<FileList | File[]>;

/**
 * Serialize a flat filter object to a query string. Preserves commas
 * unescaped so callers can pass `"a,b,c"` for multi-select params
 * (the DRF endpoints expect this form). Empty / nullish values are skipped.
 */
export function buildQueryString<T extends object>(filters: T): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value)).replace(/%2C/gi, ",")}`,
    );
  }
  return parts.join("&");
}

/**
 * Formats a file's lastModified timestamp as a local ISO string with timezone offset.
 *
 * ZIP files store timestamps as local time, so we need to preserve the user's
 * local time when uploading. Using toISOString() converts to UTC which causes
 * timezone shifts when the ZIP is created on a server in a different timezone.
 * Including the timezone offset makes the date unambiguous for the backend.
 */
export function formatLocalISODate(timestamp: number): string {
  const date = new Date(timestamp);
  const pad = (n: number) => n.toString().padStart(2, "0");

  // Get timezone offset in minutes and convert to ±HH:MM format
  const offsetMinutes = date.getTimezoneOffset();
  const offsetSign = offsetMinutes <= 0 ? "+" : "-";
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const offsetStr = `${offsetSign}${pad(offsetHours)}:${pad(offsetMins)}`;

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offsetStr}`;
}

// @pachewise: Sometimes we get the files from the `input<type="file">.files` property,
// such as with an "Upload" button. Sometimes we get it as an array of Files,
// such as from the Dropzone component. We need to handle both cases consistently
// by using multipart/form-data and appending to the relevant form field.
export function addFilesToForm(
  form: FormData,
  files: Files,
  key: string = "documents",
) {
  for (const file of files) {
    form.append(key, file);
  }
}

/**
 * Helper to parse form data from react-hook-form into a FormData object
 * @param data The raw form data as an object
 * @param fileField The field that we should iterate over (defaults to "documents")
 * @returns the FormData you will attach to a multipart/form-data request.
 */
export function createFormData<D extends Record<string, unknown>>(
  data: D,
  fileField: keyof D & string = "documents",
): FormData {
  // typeGuard + sanity check for when to iterate over a FileList / File[].
  const isFilesField = (value: unknown, key: string): value is Files =>
    !!value && key === fileField;

  const form = new FormData();
  for (const key in data) {
    const value = data[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (isFilesField(value, key)) {
      addFilesToForm(form, value, fileField);
    } else {
      form.append(key, value.toString());
    }
  }
  return form;
}

export interface ApiSuccess<T> {
  data: T;
  meta?: any;
}

export interface ApiError<T extends Record<string, any> = Record<string, any>> {
  error: string;
  errorStatus: number;
  formErrors: { [K in keyof T]: string } & {
    non_field_errors?: string;
    detail?: string;
  };
}

// Some API responses (such as bulk upload and logIn) use different keys
// for the Error responses vs the Success. Some of them don't return anything (Delete => 204).
// The second type argument accounts for that. In those cases,
// T is the shape of the `data` object for the Success, and ER should be
// an object representing the keys of the form submitted, so we can type-check
// the formErrors object.
export type ApiResponse<
  T extends Record<string, any> = Record<string, any>,
  ER extends Record<string, any> = T,
> = ApiSuccess<T> | ApiError<ER>;

// This uses type inference and you should never have to define the generics:
// they should be inferred from maybeError
export function isApiError<
  T extends Record<string, any>,
  ER extends Record<string, any> = T,
>(maybeError: ApiResponse<T, ER>): maybeError is ApiError<ER> {
  return "error" in maybeError;
}

/** Thrown by {@link unwrapOrThrow} when a response is the interceptor's
 * resolved error envelope. Carries the HTTP status and the response body
 * fields (`handleApiError`'s formErrors) so callers can branch on status
 * codes and read fields like a 409's `proposal_id`. */
export class ApiRequestError extends Error {
  status: number;
  body: Record<string, unknown>;

  constructor(detail: string, status: number, body?: Record<string, unknown>) {
    super(detail);
    this.name = "ApiRequestError";
    this.status = status;
    this.body = body ?? {};
  }
}

/** The response interceptor RESOLVES HTTP errors into `{error, errorStatus,
 * formErrors}` objects instead of rejecting, so `await apiClient.get(...)`
 * on a failed request quietly yields `response.data === undefined`. Wrap a
 * response with this to turn that envelope back into a thrown
 * {@link ApiRequestError} so React Query error paths and catch blocks
 * actually run. */
export function unwrapOrThrow<T>(response: unknown): T {
  const maybe = response as ApiResponse;
  if (typeof maybe === "object" && maybe !== null && isApiError(maybe)) {
    throw new ApiRequestError(
      maybe.error || "Server error",
      maybe.errorStatus,
      maybe.formErrors,
    );
  }
  return (response as { data: T }).data;
}

export type ApiPaginatedResponse<T = any> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  page_size: number;
  meta: object;
};

// Use by React-Query generic functions, see useInfiniteQuery<D> below for an example
export type PageParam = string | number;

export interface ApiInfinitePaginatedResponse<T = any> {
  pages: ApiPaginatedResponse<T>[];
  pageParams: Array<PageParam>;
  meta?: any;
}

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

export const apiClientNoAuth = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

export function getAccessToken() {
  return getCookie(TOKEN_KEY_NAME);
}

export function getActingOrganizationId() {
  return getCookie(CF_ORGANIZATION_KEY_NAME);
}

export function getDeviceId() {
  return getCookie(DEVICE_ID_NAME);
}

function getImpersonateId() {
  const currentUrl = window.location.href;
  const urlObj = new URL(currentUrl);
  const queryParams = new URLSearchParams(urlObj.search);
  let impersonateValue = queryParams.get("impersonateId");
  if (
    impersonateValue &&
    getCookie(IMPERSONATE_KEY_NAME) !== impersonateValue
  ) {
    setCookie(IMPERSONATE_KEY_NAME, impersonateValue);
  } else if (!impersonateValue && getCookie(IMPERSONATE_KEY_NAME)) {
    impersonateValue = getCookie(IMPERSONATE_KEY_NAME);
  }
  if (impersonateValue) {
    queryParams.delete("impersonateId");
    window.history.replaceState(
      {},
      "",
      `${urlObj.pathname}${queryParams.toString() ? `?${queryParams}` : ""}`,
    );
  }
  return impersonateValue;
}

apiClient.interceptors.request.use((request) => {
  const impersonateId = getImpersonateId();
  const token = getAccessToken();
  const organizationId = getActingOrganizationId();
  request.headers["Authorization"] = `Token ${token}`;
  request.headers[COMMONFORGE_ORG_HEADER] = organizationId;
  request.headers[COMMONFORGE_SLUG_HEADER] = getSlug();
  request.headers[IMPERSONATE_HEADER] = impersonateId;
  return request;
});

apiClient.interceptors.response.use((response) => {
  const secondsUntilExpiry =
    response.headers[RESPONSE_TOKEN_TIME_HEADER.toLowerCase()] ||
    response.headers[RESPONSE_TOKEN_TIME_HEADER];
  if (secondsUntilExpiry) {
    const adjustedExpiry = Math.max(0, parseInt(String(secondsUntilExpiry)) - 5);
    localStorage.setItem(
      TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
      (new Date().getTime() + adjustedExpiry * 1000).toString(),
    );
  }

  const aiCapStatus =
    response.headers[AI_CAP_STATUS_HEADER.toLowerCase()] ||
    response.headers[AI_CAP_STATUS_HEADER];
  const aiCapStatusUpdated =
    response.headers[AI_CAP_STATUS_UPDATED_HEADER.toLowerCase()] ||
    response.headers[AI_CAP_STATUS_UPDATED_HEADER];

  if (aiCapStatus && aiCapStatusUpdated) {
    const statusData = {
      type: aiCapStatus,
      timestamp: aiCapStatusUpdated,
    };
    localStorage.setItem(
      AI_CAP_STATUS_LOCAL_STORAGE_KEY,
      JSON.stringify(statusData),
    );
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new Event("AICapStatus"));
  }

  return response;
});

export const handleApiError = (error: any) => {
  if (error.response?.status === 403 && !error.config?.suppress403Redirect) {
    let newUrl = `${WEBAPP_PROTOCOL}://${WEBAPP_DOMAIN}`;
    if (WEBAPP_PORT) {
      newUrl += `:${WEBAPP_PORT}`;
    }
    window.location.replace(newUrl);
  }
  if (error.response) {
    const errorDetail =
      error?.response?.data?.detail ||
      error?.response?.data.non_field_errors?.[0] ||
      error?.response?.data?.[0] ||
      "Server error";
    const errorStatus = error.response.status;
    const formErrors: { [key: string]: any } = {};
    if (
      typeof error.response.data === "object" &&
      Object.keys(error.response.data).length > 0
    ) {
      for (const key in error.response.data) {
        if (Array.isArray(error.response.data[key]))
          formErrors[key] = error.response.data[key][0];
        else formErrors[key] = error.response.data[key];
      }
    }
    return {
      error: errorDetail,
      errorStatus: errorStatus,
      formErrors: Object.keys(formErrors).length === 0 ? undefined : formErrors,
    };
  }
  return {
    error: "Server error",
    errorStatus: error.response?.status || error.status || 500,
  };
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only an authentication failure means the session is gone: this API
    // also answers 401 for a permission denial, which must not log anyone
    // out. Calls made before a session exists use apiClientNoAuth.
    const detail = error.response?.data?.detail;
    const sessionGone =
      error.response?.status === 401 &&
      (detail === "Invalid token." ||
        detail === "Authentication credentials were not provided.");
    if (error.request?.responseURL?.includes("api/token/refresh/") || sessionGone) {
      // If we've already redirected once for this reason, stop — otherwise a
      // stale parent-domain cookie that `deleteCookie` can't reach (because
      // it's scoped to a different domain than WEBAPP_DOMAIN) pins us in a
      // full-page-reload loop on every API response.
      const url = new URL(window.location.href);
      if (url.searchParams.get("err") === "session_expired") {
        return Promise.reject(error);
      }
      if (getCookie(TOKEN_KEY_NAME)) {
        deleteCookie(CF_ORGANIZATION_KEY_NAME);
        deleteCookie(TOKEN_KEY_NAME);
      }
      window.location.href = `${url.protocol}//${url.host}${url.pathname}?err=session_expired`;
      return Promise.reject(error);
    } else {
      return handleApiError(error);
    }
  },
);

apiClientNoAuth.interceptors.response.use(
  (response) => response,
  (error) => {
    return handleApiError(error);
  },
);

/**
 * A wrapper around @tanstack/react-query
 * so that we can easily define the options and response type
 * based on the JSON body of the response (`response.data`).
 * @param options A configuration dict
 * @returns See https://tanstack.com/query/latest/docs/framework/react/reference/useInfiniteQuery
 */
export function useInfiniteQuery<D extends Record<string, any>>(
  options: UseInfiniteQueryOptions<
    ApiPaginatedResponse<D>,
    ApiError<D>,
    ApiInfinitePaginatedResponse<D>,
    QueryKey,
    PageParam
  >,
) {
  return useTanStackInfiniteQuery(options);
}
